import { Capacitor } from '@capacitor/core';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Plus,
  Link2,
  Copy,
  Check,
  MessageSquare,
  ChevronLeft,
  Send,
  Loader2,
  Trash2,
  Smile,
  X,
} from 'lucide-react';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { supabase } from '../../../lib/supabase';
import { getCurrentUserId } from '../../../utils/authUtils';
import { safeClipboardWrite } from '../../../utils/clipboard';

const EMOJI_LIST = ['😀', '😊', '👍', '❤️', '😂', '🎉', '👋', '✨', '🙏', '🔥', '💯', '👏', '😎', '🤔', '💪', '✅', '❌', '⭐', '🙌', '😅'];

type Cohort = {
  id: string;
  name: string;
  purpose: string | null;
  join_token: string;
  creator_id: string;
  created_at: string;
  member_count?: number;
};

type Message = {
  id: string;
  cohort_id: string;
  user_id: string | null;
  message_type: 'text' | 'system' | 'job_card';
  content: string | null;
  created_at: string;
  sender_name?: string;
};

type CohortMember = { user_id: string; display_name: string; joined_at: string };

const Collaborate: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCohortId, setSelectedCohortId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createPurpose, setCreatePurpose] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdCohort, setCreatedCohort] = useState<Cohort | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [members, setMembers] = useState<CohortMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [typingDisplayNames, setTypingDisplayNames] = useState<string[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const membersChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastMessageIdRef = useRef<string | null>(null);

  const userId = useRef<string | null>(null);
  const myDisplayNameRef = useRef<string>('Someone');

  const loadCohorts = useCallback(async () => {
    const uid = await getCurrentUserId();
    userId.current = uid;
    if (!uid) {
      setLoading(false);
      return;
    }
    const { data: memberRows, error: memberErr } = await supabase
      .from('collaborate_cohort_members')
      .select('cohort_id')
      .eq('user_id', uid);
    if (memberErr || !memberRows?.length) {
      setCohorts([]);
      setLoading(false);
      return;
    }
    const cohortIds = memberRows.map((r) => r.cohort_id);
    const { data: cohortData, error: cohortErr } = await supabase
      .from('collaborate_cohorts')
      .select('id, name, purpose, join_token, creator_id, created_at')
      .in('id', cohortIds)
      .order('created_at', { ascending: false });
    if (cohortErr) {
      setCohorts([]);
      setLoading(false);
      return;
    }
    const withCount = await Promise.all(
      (cohortData || []).map(async (c) => {
        const { count } = await supabase
          .from('collaborate_cohort_members')
          .select('*', { count: 'exact', head: true })
          .eq('cohort_id', c.id);
        return { ...c, member_count: count ?? 0 };
      })
    );
    setCohorts(withCount);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadCohorts();
  }, [loadCohorts]);

  const loadMessages = useCallback(async (cohortId: string) => {
    setMessagesLoading(true);
    const { data, error } = await supabase
      .from('collaborate_cohort_messages')
      .select('id, cohort_id, user_id, message_type, content, created_at')
      .eq('cohort_id', cohortId)
      .order('created_at', { ascending: true });
    if (error) {
      setMessages([]);
      setMessagesLoading(false);
      return;
    }
    const msgs = (data || []).map((m) => ({ ...m, sender_name: undefined }));
    setMessages(msgs);
    setMessagesLoading(false);
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, []);

  const loadMembers = useCallback(async (cohortId: string) => {
    setMembersLoading(true);
    const { data, error } = await supabase.rpc('get_cohort_members', { p_cohort_id: cohortId });
    if (error) {
      setMembers([]);
      setMembersLoading(false);
      return;
    }
    const list = (data as CohortMember[]) || [];
    setMembers(list);
    const me = list.find((m) => m.user_id === userId.current);
    if (me) myDisplayNameRef.current = me.display_name;
    setMembersLoading(false);
  }, []);

  // Polling fallback: ensure messages show even if Realtime misses events
  useEffect(() => {
    if (!selectedCohortId) return;
    const interval = setInterval(async () => {
      if (document.visibilityState !== 'visible') return;
      const { data } = await supabase
        .from('collaborate_cohort_messages')
        .select('id, cohort_id, user_id, message_type, content, created_at')
        .eq('cohort_id', selectedCohortId)
        .order('created_at', { ascending: true });
      if (data?.length) {
        setMessages((prev) => {
          const byId = new Map(prev.map((m) => [m.id, m]));
          data.forEach((m) => byId.set(m.id, m as Message));
          const merged = Array.from(byId.values()).sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
          return merged;
        });
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [selectedCohortId]);

  useEffect(() => {
    if (!selectedCohortId) {
      setMessages([]);
      setMembers([]);
      setTypingDisplayNames([]);
      if (channelRef.current) {
        try { supabase.removeChannel(channelRef.current); } catch (_) {}
        channelRef.current = null;
      }
      if (membersChannelRef.current) {
        try { supabase.removeChannel(membersChannelRef.current); } catch (_) {}
        membersChannelRef.current = null;
      }
      return;
    }
    loadMessages(selectedCohortId);
    loadMembers(selectedCohortId);

    let channel: ReturnType<typeof supabase.channel> | null = null;
    const uid = userId.current;
    // Per-user auto-clear timers: if no typing signal received for 3 s, remove that user
    const typingTimers = new Map<string, ReturnType<typeof setTimeout>>();
    const clearTypingUser = (name: string) => {
      setTypingDisplayNames((prev) => prev.filter((n) => n !== name));
      typingTimers.delete(name);
    };

    try {
      channel = supabase
        .channel(`cohort:${selectedCohortId}`, {
          // broadcast: self=false so we never see our own typing events
          config: { broadcast: { self: false } },
        })
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'collaborate_cohort_messages',
            filter: `cohort_id=eq.${selectedCohortId}`,
          },
          (payload) => {
            const newRow = payload.new as Message;
            setMessages((prev) => {
              if (prev.some((m) => m.id === newRow.id)) return prev;
              return [...prev, newRow];
            });
            setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
          }
        )
        // Broadcast-based typing indicator — reliable, no presence sync races
        .on('broadcast', { event: 'typing' }, ({ payload }: { payload: { user_id: string; display_name: string; typing: boolean } }) => {
          if (!payload?.display_name || payload.user_id === uid) return;
          const name = payload.display_name;
          if (payload.typing) {
            setTypingDisplayNames((prev) =>
              prev.includes(name) ? prev : [...prev, name]
            );
            // Refresh the auto-clear timer for this user
            if (typingTimers.has(name)) clearTimeout(typingTimers.get(name)!);
            typingTimers.set(name, setTimeout(() => clearTypingUser(name), 3000));
          } else {
            if (typingTimers.has(name)) { clearTimeout(typingTimers.get(name)!); typingTimers.delete(name); }
            setTypingDisplayNames((prev) => prev.filter((n) => n !== name));
          }
        })
        .subscribe((status, err) => {
          if (status === 'CHANNEL_ERROR' && err) {
            console.warn('[Collaborate] Realtime failed:', err?.message || err);
          }
        });
      channelRef.current = channel;
    } catch (e) {
      console.warn('[Collaborate] Realtime setup failed:', e);
      channelRef.current = null;
    }

    let membersChannel: ReturnType<typeof supabase.channel> | null = null;
    try {
      membersChannel = supabase
        .channel(`cohort-members:${selectedCohortId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'collaborate_cohort_members',
            filter: `cohort_id=eq.${selectedCohortId}`,
          },
          () => {
            loadMembers(selectedCohortId);
            loadCohorts();
          }
        )
        .subscribe((status, err) => {
          if (status === 'CHANNEL_ERROR' && err) {
            console.warn('[Collaborate] Members realtime failed:', err?.message || err);
          }
        });
      membersChannelRef.current = membersChannel;
    } catch (e) {
      membersChannelRef.current = null;
    }

    return () => {
      if (channelRef.current) {
        try { supabase.removeChannel(channelRef.current); } catch (_) {}
        channelRef.current = null;
      }
      if (membersChannelRef.current) {
        try { supabase.removeChannel(membersChannelRef.current); } catch (_) {}
        membersChannelRef.current = null;
      }
    };
  }, [selectedCohortId, loadMessages, loadMembers, loadCohorts]);

  const sendMessage = async () => {
    const text = newMessage.trim();
    if (!text || !selectedCohortId || !userId.current) return;
    setSending(true);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    channelRef.current?.send({
      type: 'broadcast',
      event: 'typing',
      payload: { user_id: userId.current, display_name: myDisplayNameRef.current, typing: false },
    });
    const { data: inserted, error } = await supabase
      .from('collaborate_cohort_messages')
      .insert({
        cohort_id: selectedCohortId,
        user_id: userId.current,
        message_type: 'text',
        content: text,
      })
      .select('id, cohort_id, user_id, message_type, content, created_at')
      .single();
    if (!error && inserted) {
      setNewMessage('');
      setMessages((prev) => {
        if (prev.some((m) => m.id === inserted.id)) return prev;
        return [...prev, inserted];
      });
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
    setSending(false);
  };

  const notifyTyping = useCallback(() => {
    const uid = userId.current;
    if (!uid || !channelRef.current) return;
    // Broadcast typing=true; auto-reset after 2.5 s of silence
    channelRef.current.send({
      type: 'broadcast',
      event: 'typing',
      payload: { user_id: uid, display_name: myDisplayNameRef.current, typing: true },
    });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      channelRef.current?.send({
        type: 'broadcast',
        event: 'typing',
        payload: { user_id: uid, display_name: myDisplayNameRef.current, typing: false },
      });
      typingTimeoutRef.current = null;
    }, 2500);
  }, []);

  const insertEmoji = (emoji: string) => {
    setNewMessage((prev) => prev + emoji);
  };

  const createCohort = async () => {
    const name = createName.trim();
    if (!name) return;
    // userId.current is set by loadCohorts but may be null if still loading — fetch fresh as fallback
    const uid = userId.current || await getCurrentUserId();
    userId.current = uid;
    if (!uid) return;
    setCreating(true);
    try {
      // Generate token client-side and retry up to 5 times to avoid unique constraint collision
      let cohort: { id: string; name: string; purpose: string | null; join_token: string; creator_id: string; created_at: string } | null = null;
      const generateToken = () => Array.from(crypto.getRandomValues(new Uint8Array(6))).map(b => b.toString(16).padStart(2, '0')).join('');
      for (let attempt = 0; attempt < 5; attempt++) {
        const { data, error: cohortErr } = await supabase
          .from('collaborate_cohorts')
          .insert({
            creator_id: uid,
            name,
            purpose: createPurpose.trim() || null,
            join_token: generateToken(),
          })
          .select('id, name, purpose, join_token, creator_id, created_at')
          .single();
        if (!cohortErr && data) { cohort = data; break; }
        // 23505 = unique violation — retry with a new token; any other error → bail
        if ((cohortErr as { code?: string })?.code !== '23505') break;
      }
      if (!cohort) return;
      await supabase.from('collaborate_cohort_members').insert({ cohort_id: cohort.id, user_id: uid });
      await supabase.from('collaborate_cohort_messages').insert({
        cohort_id: cohort.id,
        user_id: null,
        message_type: 'system',
        content: t('dashboard.collaborate.cohortCreatedMessage', { name: cohort.name }),
      });
      setCreatedCohort({ ...cohort, member_count: 1 });
      setCreateName('');
      setCreatePurpose('');
      loadCohorts();
    } finally {
      setCreating(false);
    }
  };

  const copyJoinLink = (token: string) => {
    const url = `${window.location.origin}/join/${token}`;
    safeClipboardWrite(url).then(() => {});
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleDeleteCohort = async (c: Cohort) => {
    const uid = userId.current;
    if (!uid || c.creator_id !== uid) return;
    setDeletingId(c.id);
    try {
      const { error } = await supabase
        .from('collaborate_cohorts')
        .delete()
        .eq('id', c.id)
        .eq('creator_id', uid);
      if (error) throw error;
      if (selectedCohortId === c.id) setSelectedCohortId(null);
      await loadCohorts();
    } catch {
      window.alert(t('dashboard.collaborate.deleteCohort') + ': failed. Try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const selectedCohort = selectedCohortId ? cohorts.find((c) => c.id === selectedCohortId) : null;

  const showChatOnly = !!selectedCohortId;

  return (
    <div className="ggj-light-palette h-full min-h-0 flex flex-col overflow-hidden p-3 sm:p-4 md:p-6 pt-8 sm:pt-10 w-full min-w-0">
      {!Capacitor.isNativePlatform() && (
      <div className="flex-shrink-0 mb-3 md:mb-4">
        <h1 className="text-lg sm:text-xl md:text-2xl font-bold ggj-page-title flex items-center gap-2 truncate min-w-0">
          <Users className="w-6 h-6 sm:w-7 sm:h-7 ggj-page-title-icon shrink-0" />
          {t('dashboard.collaborate.title')}
        </h1>
        <p className="text-xs sm:text-sm vscode-text-secondary mt-0.5 md:mt-1 line-clamp-2">
          {t('dashboard.collaborate.subtitle')}
        </p>
      </div>
      )}

      <div className="flex-1 min-h-0 flex flex-col md:flex-row gap-3 md:gap-4 overflow-hidden">
        {/* Sidebar: only show on md+ or when no cohort selected on mobile (list view) */}
        <div className={`flex-shrink-0 w-full md:w-72 min-h-0 vscode-bg-secondary rounded-xl border vscode-border p-3 md:p-4 flex flex-col overflow-hidden ${showChatOnly ? 'hidden md:flex' : ''}`}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold vscode-text-primary text-sm md:text-base">{t('dashboard.collaborate.yourCohorts')}</h2>
            <button
              type="button"
              onClick={() => { setShowCreateModal(true); setCreatedCohort(null); setCreateName(''); setCreatePurpose(''); setCreating(false); }}
              className="min-h-[44px] min-w-[44px] md:min-w-0 px-3 py-2 rounded-lg bg-[var(--vscode-primary)] text-white hover:opacity-90 flex items-center justify-center gap-1.5 touch-manipulation"
              aria-label={t('dashboard.collaborate.ariaCreateCohort')}
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm">{t('dashboard.collaborate.create')}</span>
            </button>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin vscode-text-tertiary" />
            </div>
          ) : cohorts.length === 0 ? (
            <p className="text-sm vscode-text-tertiary py-4">
              {t('dashboard.collaborate.noCohortsYet')}
            </p>
          ) : (
            <ul className="space-y-1 overflow-y-auto vscode-scrollbar flex-1 min-h-0">
              {cohorts.map((c) => (
                <li key={c.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setSelectedCohortId(c.id)}
                    className={`flex-1 min-w-0 text-left px-3 py-3 min-h-[48px] rounded-lg flex items-center gap-2 vscode-text-primary ${selectedCohortId === c.id ? 'vscode-bg-hover ring-1 ring-[var(--vscode-primary)]' : 'hover:vscode-bg-hover'}`}
                  >
                    <MessageSquare className="w-4 h-4 shrink-0 vscode-text-tertiary" />
                    <span className="truncate flex-1 font-medium">{c.name}</span>
                    {c.member_count != null && (
                      <span className="text-xs vscode-text-tertiary">{c.member_count}</span>
                    )}
                  </button>
                  {c.creator_id === userId.current && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleDeleteCohort(c); }}
                      disabled={deletingId === c.id}
                      className="shrink-0 p-2.5 min-h-[44px] min-w-[44px] rounded-lg text-red-500 hover:bg-red-500/10 disabled:opacity-50"
                      aria-label={t('dashboard.collaborate.ariaDeleteCohort')}
                      title={t('dashboard.collaborate.deleteCohort')}
                    >
                      {deletingId === c.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Main: Chat or empty — fixed height, content scrolls inside */}
        <div className="flex-1 min-h-0 min-w-0 vscode-bg-secondary rounded-xl border vscode-border flex flex-col overflow-hidden">
          {!selectedCohortId ? (
            <div className="flex-1 min-h-0 flex items-center justify-center p-6">
              <p className="text-sm vscode-text-tertiary">
                {t('dashboard.collaborate.selectCohortHint')}
              </p>
            </div>
          ) : (
            <>
              {/* Compact header: cohort name + Invite (copy link) */}
              <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 border-b vscode-border">
                <button
                  type="button"
                  onClick={() => setSelectedCohortId(null)}
                  className="p-1.5 rounded-lg hover:vscode-bg-hover sm:hidden"
                  aria-label={t('dashboard.collaborate.backToCohorts')}
                >
                  <ChevronLeft className="w-5 h-5 vscode-text-primary" />
                </button>
                <span className="flex-1 min-w-0 truncate font-medium vscode-text-primary text-sm">{selectedCohort?.name}</span>
                <button
                  type="button"
                  onClick={() => selectedCohort && copyJoinLink(selectedCohort.join_token)}
                  className="flex-shrink-0 px-2 py-1.5 rounded-lg text-xs font-medium vscode-text-secondary hover:vscode-bg-hover flex items-center gap-1"
                  title={t('dashboard.collaborate.copyInviteLink')}
                >
                  {selectedCohort && copiedToken === selectedCohort.join_token ? (
                    <Check className="w-3.5 h-3.5 text-green-500" />
                  ) : (
                    <Link2 className="w-3.5 h-3.5" />
                  )}
                  {t('dashboard.collaborate.invite')}
                </button>
              </div>
              {/* Who has joined — so inviter sees when friends join */}
              <div className="flex-shrink-0 px-3 py-2 border-b vscode-border bg-[var(--vscode-bg-tertiary, rgba(0,0,0,0.05))]">
                <p className="text-xs font-medium vscode-text-secondary mb-1.5">
                  {t('dashboard.collaborate.members', 'Members')} ({membersLoading ? '…' : members.length})
                </p>
                {membersLoading ? (
                  <div className="flex items-center gap-1.5 text-xs vscode-text-tertiary">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>{t('common.loading')}</span>
                  </div>
                ) : members.length > 0 ? (
                  <div className="flex flex-wrap gap-x-2 gap-y-1 text-xs vscode-text-primary">
                    {members.map((m) => (
                      <span key={m.user_id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded vscode-bg-hover">
                        {m.user_id === userId.current ? (
                          <span className="font-medium">{m.display_name} {t('dashboard.collaborate.you', '(you)')}</span>
                        ) : (
                          m.display_name
                        )}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs vscode-text-tertiary">{t('dashboard.collaborate.noMembersYet', 'No members yet. Share the invite link.')}</p>
                )}
              </div>
              {/* Chat messages — scrollable */}
              <div className="flex-1 min-h-0 overflow-y-auto p-3 vscode-scrollbar flex flex-col gap-2">
                {messagesLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin vscode-text-tertiary" />
                  </div>
                ) : (
                  <>
                    {messages.map((m) => (
                      <div
                        key={m.id}
                        className={`flex flex-col max-w-[85%] ${m.user_id === userId.current ? 'self-end items-end' : 'self-start'}`}
                      >
                        {m.message_type === 'system' ? (
                          <span className="text-xs italic vscode-text-tertiary">{m.content || t('dashboard.collaborate.systemMessage')}</span>
                        ) : (
                          <div
                            className={`rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed font-sans ${
                              m.user_id === userId.current
                                ? 'text-white'
                                : 'vscode-bg-tertiary vscode-text-primary border vscode-border'
                            }`}
                            style={m.user_id === userId.current ? {
                              background: 'var(--brand-user-msg-bg)',
                              boxShadow: 'var(--brand-user-msg-shadow)',
                            } : undefined}
                          >
                            <span className="whitespace-pre-wrap break-words">{m.content}</span>
                          </div>
                        )}
                        <span className="text-[10px] mt-0.5 vscode-text-tertiary">
                          {new Date(m.created_at).toLocaleTimeString(i18n.language || 'en', { hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </div>
                    ))}
                    {typingDisplayNames.length > 0 && (
                      <div className="self-start flex items-center gap-2 px-3 py-1.5 rounded-2xl rounded-bl-none bg-gray-100 dark:bg-gray-700/60 text-xs text-gray-600 dark:text-gray-300 max-w-[200px]">
                        <span className="inline-flex items-center gap-[3px] shrink-0">
                          <span className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-400" style={{ animation: 'typingBounce 1.2s ease-in-out infinite', animationDelay: '0s' }} />
                          <span className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-400" style={{ animation: 'typingBounce 1.2s ease-in-out infinite', animationDelay: '0.2s' }} />
                          <span className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-400" style={{ animation: 'typingBounce 1.2s ease-in-out infinite', animationDelay: '0.4s' }} />
                        </span>
                        <span className="truncate font-medium">
                          {typingDisplayNames.join(', ')} {t('dashboard.collaborate.isTyping', 'typing…')}
                        </span>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>
              {/* Emoji strip */}
              {showEmojiPicker && (
                <div className="flex-shrink-0 px-2 py-2 border-t vscode-border flex flex-wrap gap-1 vscode-bg-tertiary">
                  {EMOJI_LIST.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => insertEmoji(emoji)}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:opacity-80 text-lg touch-manipulation vscode-text-primary"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
              {/* Input fixed at bottom — touch-friendly */}
              <div className="flex-shrink-0 p-2 sm:p-3 border-t vscode-border flex gap-2 items-center">
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker((p) => !p)}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center p-2 rounded-xl vscode-bg-tertiary hover:vscode-bg-hover vscode-text-secondary"
                  aria-label={t('dashboard.collaborate.emoji', 'Emoji')}
                >
                  <Smile className="w-5 h-5" />
                </button>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    notifyTyping();
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder={t('dashboard.collaborate.typeMessagePlaceholder')}
                  className="flex-1 min-h-[44px] px-3 py-2.5 rounded-xl text-[15px] vscode-input vscode-text-primary border vscode-border focus:outline-none focus:ring-2 focus:ring-[var(--vscode-primary)]"
                  disabled={sending}
                />
                <button
                  type="button"
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sending}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center p-2 rounded-xl bg-[var(--vscode-primary)] text-white hover:opacity-90 disabled:opacity-50"
                  aria-label={t('dashboard.collaborate.ariaSend')}
                >
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create cohort modal — AnimatePresence is inside the portal so framer-motion can see the motion.div */}
      {createPortal(
        <AnimatePresence>
          {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{ backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.5)' }}
            onClick={() => !creating && setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full sm:max-w-md flex flex-col rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-700 bg-[var(--vscode-bg-elevated,white)] dark:bg-[var(--vscode-bg-secondary)]"
            >
              {/* Title bar */}
              <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-3 shrink-0 text-white" style={{ background: 'var(--brand-popup-header)' }}>
                <div className="flex items-center gap-2 min-w-0">
                  <Users className="w-4 h-4 shrink-0 opacity-90" aria-hidden />
                  <span className="text-sm font-semibold truncate">
                    {!createdCohort
                      ? t('dashboard.collaborate.createCohortTitle', 'Create a cohort')
                      : t('dashboard.collaborate.cohortCreated', 'Cohort created')}
                  </span>
                </div>
                {!creating && (
                  <button
                    type="button"
                    onClick={() => { setShowCreateModal(false); setCreatedCohort(null); }}
                    className="shrink-0 w-7 h-7 flex items-center justify-center rounded-md hover:bg-white/20 transition-colors touch-manipulation"
                    aria-label={t('dashboard.collaborate.cancel', 'Cancel')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Body */}
              <div className="p-5 sm:p-6 overflow-y-auto max-h-[70vh] sm:max-h-[75vh]">
                {!createdCohort ? (
                  <>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium vscode-text-secondary mb-1.5">
                          {t('dashboard.collaborate.nameRequired', 'Name')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={createName}
                          onChange={(e) => setCreateName(e.target.value)}
                          placeholder={t('dashboard.collaborate.namePlaceholder', 'e.g. Interview Prep Squad')}
                          className="w-full px-3 py-2.5 rounded-lg text-sm vscode-input vscode-text-primary border vscode-border focus:outline-none focus:ring-2 focus:ring-[var(--vscode-primary)] min-h-[44px]"
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium vscode-text-secondary mb-1.5">
                          {t('dashboard.collaborate.purposeOptional', 'Purpose (optional)')}
                        </label>
                        <input
                          type="text"
                          value={createPurpose}
                          onChange={(e) => setCreatePurpose(e.target.value)}
                          placeholder={t('dashboard.collaborate.purposePlaceholder', 'e.g. Interview prep, accountability')}
                          className="w-full px-3 py-2.5 rounded-lg text-sm vscode-input vscode-text-primary border vscode-border focus:outline-none focus:ring-2 focus:ring-[var(--vscode-primary)] min-h-[44px]"
                        />
                      </div>
                    </div>
                    <div className="flex gap-3 mt-6">
                      <button
                        type="button"
                        onClick={() => setShowCreateModal(false)}
                        disabled={creating}
                        className="flex-1 min-h-[44px] px-4 py-2.5 rounded-xl vscode-bg-tertiary vscode-text-primary text-sm font-medium hover:vscode-bg-hover transition-colors touch-manipulation disabled:opacity-50"
                      >
                        {t('dashboard.collaborate.cancel', 'Cancel')}
                      </button>
                      <button
                        type="button"
                        onClick={createCohort}
                        disabled={!createName.trim() || creating}
                        className="flex-1 min-h-[44px] px-4 py-2.5 rounded-xl text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 transition-all touch-manipulation"
                        style={{ background: 'var(--brand-cta-bg)' }}
                      >
                        {creating
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <span>{t('dashboard.collaborate.create', 'Create')}</span>}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm vscode-text-secondary mb-4">
                      {t('dashboard.collaborate.shareLinkHint', 'Share this link to invite others to')} <strong className="vscode-text-primary">{createdCohort.name}</strong>:
                    </p>
                    <div className="flex gap-2 mb-5">
                      <input
                        type="text"
                        readOnly
                        value={`${typeof window !== 'undefined' ? window.location.origin : ''}/join/${createdCohort.join_token}`}
                        className="flex-1 px-3 py-2.5 rounded-lg text-sm vscode-bg-tertiary vscode-text-primary border vscode-border min-h-[44px]"
                      />
                      <button
                        type="button"
                        onClick={() => copyJoinLink(createdCohort.join_token)}
                        className="min-h-[44px] min-w-[44px] flex items-center justify-center p-2 rounded-xl vscode-bg-hover border vscode-border touch-manipulation"
                        title={t('dashboard.collaborate.copyLink', 'Copy link')}
                      >
                        {copiedToken === createdCohort.join_token
                          ? <Check className="w-5 h-5 text-green-500" />
                          : <Copy className="w-5 h-5 vscode-text-primary" />}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setShowCreateModal(false); setCreatedCohort(null); setSelectedCohortId(createdCohort.id); }}
                      className="w-full min-h-[44px] px-4 py-2.5 rounded-xl text-white text-sm font-semibold hover:opacity-90 transition-all touch-manipulation"
                      style={{ background: 'var(--brand-cta-bg)' }}
                    >
                      {t('dashboard.collaborate.openChat', 'Open Chat')}
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default React.memo(Collaborate);
